import { AppError } from './AppError'
import { ErrorCode } from './ErrorCode'

interface ApolloErrorLike {
  graphQLErrors: Array<{ extensions?: { code?: string }; message?: string }>
  networkError?: Error | null
}

function isApolloError(error: unknown): error is ApolloErrorLike {
  return (
    typeof error === 'object' &&
    error !== null &&
    'graphQLErrors' in error &&
    'networkError' in error
  )
}

function isNetworkError(error: unknown): boolean {
  if (isApolloError(error)) {
    return !!error.networkError
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message: unknown }).message)
    return (
      message.includes('Network') ||
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('Failed to fetch')
    )
  }
  return false
}

function isGraphQLError(error: unknown): boolean {
  if (isApolloError(error)) {
    return error.graphQLErrors.length > 0
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message: unknown }).message)
    return (
      message.includes('GraphQL') ||
      message.includes('graphql') ||
      message.includes('query') ||
      message.includes('mutation')
    )
  }
  return false
}

function isValidationError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String(
      (error as { message: unknown }).message
    ).toLowerCase()
    return (
      message.includes('validation') ||
      message.includes('valid') ||
      message.includes('iban') ||
      message.includes('required') ||
      message.includes('invalid')
    )
  }
  return false
}

function isAuthError(error: unknown): boolean {
  if (isApolloError(error)) {
    return error.graphQLErrors.some(
      (err: { extensions?: { code?: string } }) =>
        err.extensions?.code === 'UNAUTHENTICATED' ||
        err.extensions?.code === 'FORBIDDEN'
    )
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String(
      (error as { message: unknown }).message
    ).toLowerCase()
    return (
      message.includes('unauthorized') ||
      message.includes('forbidden') ||
      message.includes('auth') ||
      message.includes('permission')
    )
  }
  return false
}

function isNotFoundError(error: unknown): boolean {
  if (isApolloError(error)) {
    return error.graphQLErrors.some(
      (err: { extensions?: { code?: string } }) =>
        err.extensions?.code === 'NOT_FOUND'
    )
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String(
      (error as { message: unknown }).message
    ).toLowerCase()
    return (
      message.includes('not found') ||
      message.includes('404') ||
      message.includes('does not exist')
    )
  }
  return false
}

function isPaymentError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String(
      (error as { message: unknown }).message
    ).toLowerCase()
    return (
      message.includes('payment') ||
      message.includes('paypal') ||
      message.includes('adyen') ||
      message.includes('checkout')
    )
  }
  return false
}

function isFileUploadError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String(
      (error as { message: unknown }).message
    ).toLowerCase()
    return (
      message.includes('upload') ||
      message.includes('file') ||
      message.includes('media')
    )
  }
  return false
}

export function handleError(error: unknown): AppError {
  if (error instanceof AppError) {
    return error
  }

  if (isNetworkError(error)) {
    return new AppError(ErrorCode.NETWORK, error)
  }

  // Check auth errors before GraphQL errors since auth errors are a subset
  if (isAuthError(error)) {
    return new AppError(ErrorCode.AUTH, error)
  }

  if (isNotFoundError(error)) {
    return new AppError(ErrorCode.NOT_FOUND, error)
  }

  if (isGraphQLError(error)) {
    return new AppError(ErrorCode.GRAPHQL, error)
  }

  if (isValidationError(error)) {
    return new AppError(ErrorCode.VALIDATION, error)
  }

  if (isPaymentError(error)) {
    return new AppError(ErrorCode.PAYMENT, error)
  }

  if (isFileUploadError(error)) {
    return new AppError(ErrorCode.FILE_UPLOAD, error)
  }

  return new AppError(ErrorCode.UNKNOWN, error)
}
